import { Flex, Grid, GridItem } from '@chakra-ui/react'
import { Box, Divider, FontPicker, Switch, Text, ThemePreview } from 'components'
import { SingleUseMediaInput } from 'components/media'
import { deleteFile, forceLight, useBreakpoint } from 'utils'
import { useWorkspace } from '../providers'

const ICON_FILE_PATH = 'branding/icon'
const ICON_FILE_PATH_DARK = 'branding/icon-dark'
const LOGO_FILE_PATH = 'branding/logo'
const LOGO_FILE_PATH_DARK = 'branding/logo-dark'

export default function WorkspaceBranding() {
  const { updateWorkspace, workspace } = useWorkspace()
  const isMobile = useBreakpoint() < 2

  if (!workspace) return null
  const { hideBranding, iconPath, iconPathDark, logoPath, logoPathDark } = workspace.branding || {}

  async function handleClose(action: 'save' | 'cancel' | 'delete', path: string) {
    if (action === 'save') {
      await updateWorkspace({ branding: { ...workspace!.branding, logoPath: path } })
    } else if (action === 'delete') {
      await updateWorkspace({ branding: { ...workspace!.branding, logoPath: undefined } })
      await deleteFile(`Workspaces/${workspace!.id}/${path}`).catch(() => {})
    }
  }

  const logoLightInput = (
    <SingleUseMediaInput
      compact
      defaultImage="/common/momentify-logo-dark.svg"
      filePath={LOGO_FILE_PATH}
      imageProps={{ borderRadius: 0, objectFit: 'contain' }}
      label=""
      noFile={!logoPath}
      onClose={action => handleClose(action, LOGO_FILE_PATH)}
      type="image"
    />
  )

  const logoDarkInput = (
    <SingleUseMediaInput
      compact
      defaultImage={logoPath ?? '/common/momentify-logo-light.svg'}
      filePath={LOGO_FILE_PATH_DARK}
      imageProps={{ borderRadius: 0, objectFit: 'contain' }}
      label=""
      noFile={!logoPathDark}
      onClose={action => handleClose(action, LOGO_FILE_PATH_DARK)}
      type="image"
    />
  )

  const iconLightInput = (
    <SingleUseMediaInput
      compact
      defaultImage="/common/momentify-icon-dark.svg"
      filePath={ICON_FILE_PATH}
      imageProps={{ borderRadius: 0, objectFit: 'contain' }}
      label=""
      noFile={!iconPath}
      onClose={action => handleClose(action, ICON_FILE_PATH)}
      type="image"
    />
  )

  const iconDarkInput = (
    <SingleUseMediaInput
      compact
      defaultImage={iconPath ?? '/common/momentify-icon-light.svg'}
      filePath={ICON_FILE_PATH_DARK}
      imageProps={{ borderRadius: 0, objectFit: 'contain' }}
      label=""
      noFile={!iconPathDark}
      onClose={action => handleClose(action, ICON_FILE_PATH_DARK)}
      type="image"
    />
  )

  const rowDescriptionProps = {
    maxW: '100%',
    opacity: 0.65,
    overflowWrap: 'break-word' as const,
    textStyle: 'bodySmall' as const,
    whiteSpace: 'normal' as const,
  }

  const logoRowLabel = (
    <Flex direction="column" gap={1} minW={0} pr={{ md: 2 }}>
      <Text fontWeight="500" textStyle="bodyMedium">
        Logo
      </Text>
      <Text {...rowDescriptionProps}>Shown when the sidebar is expanded.</Text>
    </Flex>
  )

  const iconRowLabel = (
    <Flex direction="column" gap={1} minW={0} pr={{ md: 2 }}>
      <Text fontWeight="500" textStyle="bodyMedium">
        Icon
      </Text>
      <Text {...rowDescriptionProps}>
        Required while a custom logo is set. Shown in place of logo when the sidebar is collapsed.
      </Text>
    </Flex>
  )

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      mt={0.5}
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
      overflowY="auto"
      overflowX="hidden"
    >
      <Text textStyle="h3" pt={2}>
        Workspace Branding
      </Text>
      <Box
        {...forceLight}
        display="flex"
        bg="linear-gradient(167deg, rgba(255, 255, 255, 0.70) 2.36%, rgba(255, 255, 255, 0.60) 92.17%)"
        px={{ base: 4, sm: 14 }}
        py={10}
        gap={6}
        borderRadius="xl"
        border="1px solid #FFF"
        boxShadow="0px 7px 16px 0px rgba(0, 0, 0, 0.08)"
        alignItems="flex-start"
        flexDirection="column"
        backdropFilter="blur(40px)"
        justifyContent="center"
      >
        {!isMobile ? (
          <Grid
            alignItems="stretch"
            gap={4}
            templateColumns="minmax(8rem, 14rem) minmax(0, 1fr) minmax(0, 1fr)"
            width="100%"
          >
            <GridItem />
            <GridItem>
              <Text opacity={0.7} textAlign="center" textStyle="bodySmall">
                Light
              </Text>
            </GridItem>
            <GridItem>
              <Text opacity={0.7} textAlign="center" textStyle="bodySmall">
                Dark
              </Text>
            </GridItem>
            <GridItem alignSelf="start" minW={0}>
              {logoRowLabel}
            </GridItem>
            <GridItem>
              <ThemePreview mode="light">{logoLightInput}</ThemePreview>
            </GridItem>
            <GridItem>
              <ThemePreview mode="dark">{logoDarkInput}</ThemePreview>
            </GridItem>
            <GridItem alignSelf="start" minW={0}>
              {iconRowLabel}
            </GridItem>
            <GridItem>
              <ThemePreview mode="light">{iconLightInput}</ThemePreview>
            </GridItem>
            <GridItem>
              <ThemePreview mode="dark">{iconDarkInput}</ThemePreview>
            </GridItem>
          </Grid>
        ) : (
          <Flex direction="column" gap={6} width="100%">
            <Flex direction="column" gap={3}>
              {logoRowLabel}
              <Flex direction={isMobile ? 'column' : 'row'} gap={5} minWidth={0}>
                <Flex direction="column" flex={1} gap={2} minWidth={0}>
                  <Text opacity={0.7} textStyle="bodySmall">
                    Light
                  </Text>
                  <ThemePreview mode="light">{logoLightInput}</ThemePreview>
                </Flex>
                <Flex direction="column" flex={1} gap={2} minWidth={0}>
                  <Text opacity={0.7} textStyle="bodySmall">
                    Dark
                  </Text>
                  <ThemePreview mode="dark">{logoDarkInput}</ThemePreview>
                </Flex>
              </Flex>
            </Flex>
            <Divider borderColor="blackAlpha.200" />
            <Flex direction="column" gap={3}>
              {iconRowLabel}
              <Flex direction={isMobile ? 'column' : 'row'} gap={5} minWidth={0}>
                <Flex direction="column" flex={1} gap={2} minWidth={0}>
                  <Text opacity={0.7} textStyle="bodySmall">
                    Light
                  </Text>
                  <ThemePreview mode="light">{iconLightInput}</ThemePreview>
                </Flex>
                <Flex direction="column" flex={1} gap={2} minWidth={0}>
                  <Text opacity={0.7} textStyle="bodySmall">
                    Dark
                  </Text>
                  <ThemePreview mode="dark">{iconDarkInput}</ThemePreview>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        )}

        <Divider borderColor="blackAlpha.200" width="100%" />

        <Flex direction="column" gap={4} width="100%">
          <Text fontWeight="500" textStyle="bodyMedium">
            Appearance
          </Text>
          <Switch
            label="Show Momentify Branding"
            labelProps={{ textStyle: 'bodyMedium', fontWeight: '500' }}
            checked={!hideBranding}
            onChange={() =>
              updateWorkspace({ branding: { ...workspace.branding, hideBranding: !hideBranding } })
            }
          />
          <FontPicker />
        </Flex>
      </Box>
    </Flex>
  )
}
